import { useState, useEffect, useMemo } from "react";
import { GenerateVizaButton } from "./GenerateVizaButton";
import { useForm } from "react-hook-form";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import {
  Loader2,
  FileText,
  Check,
  CheckCircle,
  XCircle,
  Trash2,
  Stethoscope,
  ClipboardList,
  AlertCircle,
  Plus,
  X,
} from "lucide-react";
import { patientApi } from "../services/api";
import { toast } from "sonner";
import { formatDate } from "../lib/utils";

// Calcul vârstă
const calculateAge = (birthDate) => {
  if (!birthDate) return null;
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

// Sisteme cu opțiune Normal / Modificat
const SISTEME = [
  { key: "tegumente", label: "Tegumente și mucoase", normalText: "Normal colorate" },
  { key: "ganglionar", label: "Sistem ganglionar", normalText: "Ganglioni periferici nepalpabili" },
  { key: "locomotor", label: "Aparat locomotor", normalText: "Integru morfofuncțional" },
  { key: "respirator", label: "Aparat respirator", normalText: "Torace normal conformat, mișcări respiratorii egale și simetrice, murmur vezicular prezent bilateral, fără zgomote supraadăugate" },
  { key: "cardiovascular", label: "Aparat cardiovascular", normalText: "Șoc apexian spațiu 5 IC stâng pe linia medioclaviculară, zgomote cardiace ritmice, fără sufluri, artere periferice permeabile" },
  { key: "digestiv", label: "Aparat digestiv", normalText: "Abdomen mobil cu respirația, nedureros spontan sau la palpare, ficat la rebord, splină nepalpabilă, apetit prezent, tranzit păstrat" },
  { key: "urogenital", label: "Aparat urogenital", normalText: "Loje renale libere, rinichi nepalpabili, Giordano negativ bilateral, micțiuni fiziologice" },
  { key: "endocrin", label: "Sistem endocrin", normalText: "Normal clinic" },
];

// Determinare interpretare IMC pentru țesut celular subcutanat
const interpretIMCTesut = (imc, age) => {
  if (!imc || imc <= 0) return null;
  if (imc < 20) {
    return {
      descriere: "Țesut adipos slab reprezentat (subponderal)",
      status: "subponderal",
      monitorizare: false,
      nutritie: false,
      endocrin: false,
    };
  }
  if (imc < 25) {
    return {
      descriere: "Țesut adipos normal reprezentat",
      status: "normoponderal",
      monitorizare: false,
      nutritie: false,
      endocrin: false,
    };
  }
  if (imc < 30) {
    return {
      descriere: "Țesut adipos bine reprezentat (supraponderal)",
      status: "supraponderal",
      monitorizare: true,
      nutritie: false,
      endocrin: false,
    };
  }
  // Obezitate (≥ 30)
  return {
    descriere: "Obezitate",
    status: "obezitate",
    monitorizare: false,
    nutritie: true,
    endocrin: imc > 35 && age !== null && age < 16,
  };
};

const getDefaultValues = (a) => ({
  completat: a?.completat || false,
  data_completare: a?.data_completare || "",
  tegumente_status: a?.tegumente_status || "Normal",
  tegumente_descriere: a?.tegumente_descriere || "",
  teg_dermatologie: a?.teg_dermatologie || false,
  tesut_descriere: a?.tesut_descriere || "",
  tesut_status: a?.tesut_status || "",
  tesut_monitorizare: a?.tesut_monitorizare || false,
  tesut_nutritie: a?.tesut_nutritie || false,
  tesut_endocrin: a?.tesut_endocrin || false,
  ganglionar_status: a?.ganglionar_status || "Normal",
  ganglionar_descriere: a?.ganglionar_descriere || "",
  locomotor_status: a?.locomotor_status || "Normal",
  locomotor_descriere: a?.locomotor_descriere || "",
  loc_repaus: a?.loc_repaus || false,
  loc_repaus_sapt: a?.loc_repaus_sapt || "",
  loc_investigatii: a?.loc_investigatii || false,
  loc_investigatii_text: a?.loc_investigatii_text || "",
  loc_reevaluare: a?.loc_reevaluare || false,
  loc_reevaluare_sapt: a?.loc_reevaluare_sapt || "",
  loc_ortopedie_ped: a?.loc_ortopedie_ped || false,
  loc_ortopedie: a?.loc_ortopedie || false,
  respirator_status: a?.respirator_status || "Normal",
  respirator_descriere: a?.respirator_descriere || "",
  resp_pediatrie: a?.resp_pediatrie || false,
  resp_rx: a?.resp_rx || false,
  resp_probe: a?.resp_probe || false,
  resp_repaus: a?.resp_repaus || false,
  resp_repaus_zile: a?.resp_repaus_zile || "",
  cardiovascular_status: a?.cardiovascular_status || "Normal",
  cardiovascular_descriere: a?.cardiovascular_descriere || "",
  cv_cardiologie_ped: a?.cv_cardiologie_ped || false,
  cv_cardiologie: a?.cv_cardiologie || false,
  cv_ecocardiografie: a?.cv_ecocardiografie || false,
  cv_holter_ekg: a?.cv_holter_ekg || false,
  cv_holter_ta: a?.cv_holter_ta || false,
  cv_in_observatie: a?.cv_in_observatie || false,
  digestiv_status: a?.digestiv_status || "Normal",
  digestiv_descriere: a?.digestiv_descriere || "",
  dig_ecografie: a?.dig_ecografie || false,
  dig_gastro: a?.dig_gastro || false,
  urogenital_status: a?.urogenital_status || "Normal",
  urogenital_descriere: a?.urogenital_descriere || "",
  uro_urologie: a?.uro_urologie || false,
  uro_nefrologie: a?.uro_nefrologie || false,
  endocrin_status: a?.endocrin_status || "Normal",
  endocrin_descriere: a?.endocrin_descriere || "",
  endo_endocrinologie: a?.endo_endocrinologie || false,
  // Acuitate vizuala
  acuitate_vizuala_status: a?.acuitate_vizuala_status || "Normal",
  acuitate_vizuala_optie: a?.acuitate_vizuala_optie || "",  // "corectie_existenta" | "necesita_corectie"
  acuitate_vizuala_od: a?.acuitate_vizuala_od || "",
  acuitate_vizuala_os: a?.acuitate_vizuala_os || "",
  // Vedere cromatica (Normal / Discromat)
  vedere_cromatica_status: a?.vedere_cromatica_status || "Normal",
  // Sistem nervos
  sistem_nervos_status: a?.sistem_nervos_status || "Normal",
  sistem_nervos_afectiuni_preexistente: a?.sistem_nervos_afectiuni_preexistente || false,
  sistem_nervos_debut_recent: a?.sistem_nervos_debut_recent || false,
  // Proba Romberg (Pozitiv / Negativ)
  proba_romberg_status: a?.proba_romberg_status || "Negativ",
  // Examen cavitate orala
  cavitate_orala_status: a?.cavitate_orala_status || "Normal",
  cavitate_carii_superficiale: a?.cavitate_carii_superficiale || false,
  cavitate_carii_profunde: a?.cavitate_carii_profunde || false,
  cavitate_aparat_ortodontic: a?.cavitate_aparat_ortodontic || false,
  cavitate_eritem_faringe: a?.cavitate_eritem_faringe || false,
  cavitate_hipertrofie_stanga: a?.cavitate_hipertrofie_stanga || false,
  cavitate_hipertrofie_dreapta: a?.cavitate_hipertrofie_dreapta || false,
  cavitate_hipertrofie_bilaterala: a?.cavitate_hipertrofie_bilaterala || false,
  normal_clinic: a?.normal_clinic || false,
  asimptomatic: a?.asimptomatic || false,
  observatii: a?.observatii || "",
});

// Sistem panel - Normal / Modificat toggle + descriere + recomandări
// Definit inline mai jos în renderSistem (evită rerenderizări inutile)

// CheckOpt - bifă pentru recomandare cu opțional input inline
const CheckOpt = ({ id, label, checked, onChange, children, testId }) => (
  <div className="flex flex-col gap-1.5 p-2 rounded border border-slate-200 bg-white">
    <div className="flex items-center gap-2">
      <Checkbox id={id} checked={checked} onCheckedChange={onChange} data-testid={testId} />
      <Label htmlFor={id} className="text-sm cursor-pointer">{label}</Label>
    </div>
    {checked && children}
  </div>
);

// Recomandări/observații din celelalte ateliere (1, 2, 3, 5) pentru recapitulare în Atelier 4
const nonEmpty = (v) => v !== undefined && v !== null && String(v).trim() !== "";

const buildOtherAteliereItems = (patient) => {
  const items = [];
  const a1 = patient?.atelier_1 || {};
  const a2 = patient?.atelier_2 || {};
  const a3 = patient?.atelier_3 || {};
  const a5 = patient?.atelier_5 || {};

  // Atelier 1
  if (nonEmpty(a1.recomandari_posturale))
    items.push({ id: "a1-post", source: "Atelier 1 · Recomandări posturale", text: a1.recomandari_posturale.trim(), atelier: 1, patch: { recomandari_posturale: "" } });
  if (nonEmpty(a1.recomandare_sport))
    items.push({ id: "a1-sport", source: "Atelier 1 · Recomandare sport", text: a1.recomandare_sport.trim(), atelier: 1, patch: { recomandare_sport: "" } });
  if (nonEmpty(a1.recomandare_nutritie))
    items.push({ id: "a1-nutr", source: "Atelier 1 · Nutriție", text: a1.recomandare_nutritie.trim(), atelier: 1, patch: { recomandare_nutritie: "" } });
  if (nonEmpty(a1.observatii))
    items.push({ id: "a1-obs", source: "Atelier 1 · Observații", text: a1.observatii.trim(), atelier: 1, patch: { observatii: "" } });

  // Atelier 2
  if ((a2.ruffier_status === "nesatisfacator" || a2.ruffier_status === "slab") && !a2.ruffier_recomandare_dismissed) {
    const t = a2.ruffier_status === "slab"
      ? "Monitorizarea sportivului în timpul antrenamentului (adaptare foarte slabă)"
      : "Monitorizare puls";
    items.push({ id: "a2-ruf", source: "Atelier 2 · Ruffier", text: t, atelier: 2, patch: { ruffier_recomandare_dismissed: true } });
  }
  if (nonEmpty(a2.astrand_revenire_recomandare) && !a2.astrand_recomandare_dismissed)
    items.push({ id: "a2-astr", source: "Atelier 2 · Astrand (revenire)", text: a2.astrand_revenire_recomandare.trim(), atelier: 2, patch: { astrand_recomandare_dismissed: true } });
  if (nonEmpty(a2.observatii))
    items.push({ id: "a2-obs", source: "Atelier 2 · Observații", text: a2.observatii.trim(), atelier: 2, patch: { observatii: "" } });

  // Atelier 3
  if (nonEmpty(a3.observatii))
    items.push({ id: "a3-obs", source: "Atelier 3 · Observații", text: a3.observatii.trim(), atelier: 3, patch: { observatii: "" } });
  if (a3.traseu_limite_normale)
    items.push({ id: "a3-traseu", source: "Atelier 3 · EKG", text: `Traseu în limite normale cu AV: ${a3.traseu_limite_normale_av ?? "___"}`, atelier: 3, patch: { traseu_limite_normale: false } });
  if (a3.revenire_ekg)
    items.push({ id: "a3-rev", source: "Atelier 3 · EKG", text: `Revenire la EKG la ${a3.revenire_ekg_zile ?? "___"} zile`, atelier: 3, patch: { revenire_ekg: false } });

  // Atelier 5
  if (nonEmpty(a5.recomandari))
    items.push({ id: "a5-rec", source: "Atelier 5 · Recomandări", text: a5.recomandari.trim(), atelier: 5, patch: { recomandari: "" } });
  if (nonEmpty(a5.concluzii))
    items.push({ id: "a5-conc", source: "Atelier 5 · Concluzii", text: a5.concluzii.trim(), atelier: 5, patch: { concluzii: "" } });
  if (nonEmpty(a5.observatii))
    items.push({ id: "a5-obs", source: "Atelier 5 · Observații", text: a5.observatii.trim(), atelier: 5, patch: { observatii: "" } });

  return items;
};

export const Atelier4Button = ({ patientId, patientData, atelierData, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const imc = parseFloat(patientData?.atelier_1?.imc) || 0;
  const age = calculateAge(patientData?.date_personale?.data_nasterii || "");

  const { register, handleSubmit, setValue, watch, reset } = useForm({
    defaultValues: getDefaultValues(atelierData),
  });

  const watchedValues = watch();

  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (isOpen) {
      // Combinăm datele salvate cu interpretarea IMC actuală (Atelier 1)
      const base = getDefaultValues(atelierData);
      const interp = interpretIMCTesut(imc, age);
      if (interp) {
        base.tesut_descriere = interp.descriere;
        base.tesut_status = interp.status;
        base.tesut_monitorizare = interp.monitorizare;
        base.tesut_nutritie = interp.nutritie;
        base.tesut_endocrin = interp.endocrin;
      }
      reset(base);
    }
  };

  // Auto-populare țesut celular subcutanat când se schimbă IMC/vârstă cu modal deschis
  useEffect(() => {
    if (!open) return;
    const interp = interpretIMCTesut(imc, age);
    if (!interp) return;
    setValue("tesut_descriere", interp.descriere);
    setValue("tesut_status", interp.status);
    setValue("tesut_monitorizare", interp.monitorizare);
    setValue("tesut_nutritie", interp.nutritie);
    setValue("tesut_endocrin", interp.endocrin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imc, age]);

  // Când sistemul devine Modificat și are 1 singură recomandare auto, o activăm
  useEffect(() => {
    if (watchedValues.tegumente_status === "Modificat" && !watchedValues.teg_dermatologie) {
      // doar la prima activare; nu re-activăm dacă utilizatorul a șters
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-active reco pentru sisteme cu o singură recomandare la prima trecere pe Modificat
  // Iar la trecerea înapoi pe Normal, dezactivează automat TOATE recomandările acelui sistem
  const handleStatusChange = (key, value) => {
    setValue(`${key}_status`, value);
    if (value === "Modificat") {
      if (key === "tegumente" && !watchedValues.teg_dermatologie) setValue("teg_dermatologie", true);
      if (key === "endocrin" && !watchedValues.endo_endocrinologie) setValue("endo_endocrinologie", true);
    } else if (value === "Normal") {
      // Cleanup: șterge descrierea + recomandările acestui sistem
      setValue(`${key}_descriere`, "");
      if (key === "tegumente") {
        setValue("teg_dermatologie", false);
      }
      if (key === "locomotor") {
        setValue("loc_repaus", false);
        setValue("loc_repaus_sapt", "");
        setValue("loc_investigatii", false);
        setValue("loc_investigatii_text", "");
        setValue("loc_reevaluare", false);
        setValue("loc_reevaluare_sapt", "");
        setValue("loc_ortopedie_ped", false);
        setValue("loc_ortopedie", false);
      }
      if (key === "respirator") {
        setValue("resp_pediatrie", false);
        setValue("resp_rx", false);
        setValue("resp_probe", false);
        setValue("resp_repaus", false);
        setValue("resp_repaus_zile", "");
      }
      if (key === "cardiovascular") {
        setValue("cv_cardiologie_ped", false);
        setValue("cv_cardiologie", false);
        setValue("cv_ecocardiografie", false);
        setValue("cv_holter_ekg", false);
        setValue("cv_holter_ta", false);
        setValue("cv_in_observatie", false);
      }
      if (key === "digestiv") {
        setValue("dig_ecografie", false);
        setValue("dig_gastro", false);
      }
      if (key === "urogenital") {
        setValue("uro_urologie", false);
        setValue("uro_nefrologie", false);
      }
      if (key === "endocrin") {
        setValue("endo_endocrinologie", false);
      }
    }
  };

  // Date clinostatism din Atelier 2 pentru Aparat cardiovascular
  const avClino = patientData?.atelier_2?.av_clinostatism;
  const taClino = patientData?.atelier_2?.ta_clinostatism;

  // Wrapper pentru SistemPanel ca să folosească handleStatusChange
  const renderSistem = (sistem, children) => {
    const isModif = watchedValues[`${sistem.key}_status`] === "Modificat";
    const isCardio = sistem.key === "cardiovascular";
    return (
      <Card className={isModif ? "border-red-200" : "border-emerald-200"}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              {isModif ? (
                <XCircle className="w-5 h-5 text-red-500" />
              ) : (
                <CheckCircle className="w-5 h-5 text-emerald-500" />
              )}
              <CardTitle className="text-base">{sistem.label}</CardTitle>
            </div>
            <div className="flex gap-1">
              <Button
                type="button"
                variant={watchedValues[`${sistem.key}_status`] === "Normal" ? "default" : "outline"}
                size="sm"
                onClick={() => handleStatusChange(sistem.key, "Normal")}
                data-testid={`atelier-4-${sistem.key}-normal`}
              >
                Normal
              </Button>
              <Button
                type="button"
                variant={isModif ? "destructive" : "outline"}
                size="sm"
                onClick={() => handleStatusChange(sistem.key, "Modificat")}
                data-testid={`atelier-4-${sistem.key}-modif`}
              >
                Modifică
              </Button>
            </div>
          </div>
        </CardHeader>
        {!isModif && sistem.normalText && (
          <CardContent className="pt-0">
            <p className="text-sm text-slate-700 italic" data-testid={`atelier-4-${sistem.key}-normal-text`}>
              {sistem.normalText}
            </p>
            {isCardio && (avClino || taClino) && (
              <div className="mt-2 pt-2 border-t grid grid-cols-2 gap-2 text-sm">
                {avClino ? (
                  <div>
                    <span className="text-muted-foreground">AV clinostatism:</span>{" "}
                    <strong>{avClino} bpm</strong>
                  </div>
                ) : null}
                {taClino ? (
                  <div>
                    <span className="text-muted-foreground">TA clinostatism:</span>{" "}
                    <strong>{taClino}</strong>
                  </div>
                ) : null}
                <p className="col-span-2 text-xs text-muted-foreground">(preluate din Atelier 2)</p>
              </div>
            )}
          </CardContent>
        )}
        {isModif && (
          <CardContent className="space-y-3">
            <Textarea
              rows={2}
              placeholder="Descriere modificare..."
              data-testid={`atelier-4-${sistem.key}-text`}
              {...register(`${sistem.key}_descriere`)}
            />
            {children}
          </CardContent>
        )}
      </Card>
    );
  };

  // Lista recomandărilor active (pentru afișare panel + ștergere)
  const recomandari = useMemo(() => {
    const list = [];
    // Tegumente
    if (watchedValues.tegumente_status === "Modificat" && watchedValues.teg_dermatologie) {
      list.push({
        id: "teg_dermatologie",
        text: "Se recomandă control și tratament de specialitate serviciu Dermatologie",
        clear: () => setValue("teg_dermatologie", false),
      });
    }
    // Țesut celular subcutanat
    if (watchedValues.tesut_monitorizare) {
      list.push({
        id: "tesut_monitorizare",
        text: "Se recomandă monitorizarea greutății corporale",
        clear: () => setValue("tesut_monitorizare", false),
      });
    }
    if (watchedValues.tesut_nutritie) {
      list.push({
        id: "tesut_nutritie",
        text: "Se recomandă consult și tratament de specialitate serviciu Nutriție",
        clear: () => setValue("tesut_nutritie", false),
      });
    }
    if (watchedValues.tesut_endocrin) {
      list.push({
        id: "tesut_endocrin",
        text: "Se recomandă consult și tratament de specialitate serviciu Endocrinologie",
        clear: () => setValue("tesut_endocrin", false),
      });
    }
    // Aparat locomotor
    if (watchedValues.locomotor_status === "Modificat") {
      if (watchedValues.loc_repaus) {
        list.push({
          id: "loc_repaus",
          text: `Se recomandă repaus segmentar ${watchedValues.loc_repaus_sapt || "___"} săptămâni`,
          clear: () => setValue("loc_repaus", false),
        });
      }
      if (watchedValues.loc_investigatii) {
        list.push({
          id: "loc_investigatii",
          text: `Se recomandă investigații imagistice suplimentare${watchedValues.loc_investigatii_text ? ": " + watchedValues.loc_investigatii_text : ""}`,
          clear: () => setValue("loc_investigatii", false),
        });
      }
      if (watchedValues.loc_reevaluare) {
        list.push({
          id: "loc_reevaluare",
          text: `Se recomandă reevaluare în ${watchedValues.loc_reevaluare_sapt || "___"} săptămâni`,
          clear: () => setValue("loc_reevaluare", false),
        });
      }
      if (watchedValues.loc_ortopedie_ped) {
        list.push({
          id: "loc_ortopedie_ped",
          text: "Se recomandă consult și tratament de specialitate serviciu Ortopedie pediatrică",
          clear: () => setValue("loc_ortopedie_ped", false),
        });
      }
      if (watchedValues.loc_ortopedie) {
        list.push({
          id: "loc_ortopedie",
          text: "Se recomandă consult și tratament de specialitate serviciu Ortopedie",
          clear: () => setValue("loc_ortopedie", false),
        });
      }
    }
    // Aparat respirator
    if (watchedValues.respirator_status === "Modificat") {
      if (watchedValues.resp_pediatrie)
        list.push({ id: "resp_pediatrie", text: "Se recomandă consult și tratament de specialitate serviciu Pediatrie", clear: () => setValue("resp_pediatrie", false) });
      if (watchedValues.resp_rx)
        list.push({ id: "resp_rx", text: "Se recomandă RX cord-pulmon", clear: () => setValue("resp_rx", false) });
      if (watchedValues.resp_probe)
        list.push({ id: "resp_probe", text: "Se recomandă probe ventilatorii", clear: () => setValue("resp_probe", false) });
      if (watchedValues.resp_repaus)
        list.push({
          id: "resp_repaus",
          text: `Se recomandă repaus, izolare și tratament la domiciliu pe o perioadă de ${watchedValues.resp_repaus_zile || "___"} zile`,
          clear: () => setValue("resp_repaus", false),
        });
    }
    // Aparat cardiovascular
    if (watchedValues.cardiovascular_status === "Modificat") {
      if (watchedValues.cv_cardiologie_ped)
        list.push({ id: "cv_cardiologie_ped", text: "Se recomandă consult de specialitate serviciu Cardiologie pediatrică", clear: () => setValue("cv_cardiologie_ped", false) });
      if (watchedValues.cv_cardiologie)
        list.push({ id: "cv_cardiologie", text: "Se recomandă consult de specialitate Cardiologie", clear: () => setValue("cv_cardiologie", false) });
      if (watchedValues.cv_ecocardiografie)
        list.push({ id: "cv_ecocardiografie", text: "Se recomandă Ecocardiografie (Ecodoppler)", clear: () => setValue("cv_ecocardiografie", false) });
      if (watchedValues.cv_holter_ekg)
        list.push({ id: "cv_holter_ekg", text: "Se recomandă Holter EKG (24 de ore)", clear: () => setValue("cv_holter_ekg", false) });
      if (watchedValues.cv_holter_ta)
        list.push({ id: "cv_holter_ta", text: "Se recomandă Holter TA (24 de ore)", clear: () => setValue("cv_holter_ta", false) });
      if (watchedValues.cv_in_observatie)
        list.push({ id: "cv_in_observatie", text: "În observație (suflu sistolic) — reevaluare semestrială", clear: () => setValue("cv_in_observatie", false) });
    }
    // Aparat digestiv
    if (watchedValues.digestiv_status === "Modificat") {
      if (watchedValues.dig_ecografie)
        list.push({ id: "dig_ecografie", text: "Se recomandă Ecografie abdominală", clear: () => setValue("dig_ecografie", false) });
      if (watchedValues.dig_gastro)
        list.push({ id: "dig_gastro", text: "Se recomandă control de specialitate Gastroenterologie", clear: () => setValue("dig_gastro", false) });
    }
    // Aparat urogenital
    if (watchedValues.urogenital_status === "Modificat") {
      if (watchedValues.uro_urologie)
        list.push({ id: "uro_urologie", text: "Se recomandă control de specialitate serviciu Urologie", clear: () => setValue("uro_urologie", false) });
      if (watchedValues.uro_nefrologie)
        list.push({ id: "uro_nefrologie", text: "Se recomandă control de specialitate serviciu Nefrologie", clear: () => setValue("uro_nefrologie", false) });
    }
    // Sistem endocrin
    if (watchedValues.endocrin_status === "Modificat" && watchedValues.endo_endocrinologie) {
      list.push({
        id: "endo_endocrinologie",
        text: "Control de specialitate Endocrinologie",
        clear: () => setValue("endo_endocrinologie", false),
      });
    }
    // Acuitate vizuală
    if (watchedValues.acuitate_vizuala_status === "Modificat") {
      list.push({
        id: "acuitate_oftalmologic",
        text: "Consult oftalmologic periodic",
        clear: () => setValue("acuitate_vizuala_status", "Normal"),
      });
    }
    // Sistem nervos
    if (watchedValues.sistem_nervos_status === "Modificat" && watchedValues.sistem_nervos_afectiuni_preexistente) {
      list.push({
        id: "sn_preexistente",
        text: "Se cere document medical prin care se atestă acordul medicului curant neurolog pentru sportul de performanță",
        clear: () => setValue("sistem_nervos_afectiuni_preexistente", false),
      });
    }
    if (watchedValues.sistem_nervos_status === "Modificat" && watchedValues.sistem_nervos_debut_recent) {
      list.push({
        id: "sn_debut_recent",
        text: "Consult neurologic",
        clear: () => setValue("sistem_nervos_debut_recent", false),
      });
    }
    // Examen cavitate orală — carii → stomatolog (1 recomandare)
    if (
      watchedValues.cavitate_orala_status === "Modificat" &&
      (watchedValues.cavitate_carii_superficiale || watchedValues.cavitate_carii_profunde)
    ) {
      list.push({
        id: "cav_stomato",
        text: "Consult și tratament stomatologic (pentru asanarea focarelor infecțioase)",
        clear: () => {
          setValue("cavitate_carii_superficiale", false);
          setValue("cavitate_carii_profunde", false);
        },
      });
    }
    // Examen cavitate orală — hipertrofie amigdaliană → ORL (1 recomandare)
    if (
      watchedValues.cavitate_orala_status === "Modificat" &&
      (watchedValues.cavitate_hipertrofie_stanga ||
        watchedValues.cavitate_hipertrofie_dreapta ||
        watchedValues.cavitate_hipertrofie_bilaterala)
    ) {
      list.push({
        id: "cav_orl",
        text: "Consult ORL",
        clear: () => {
          setValue("cavitate_hipertrofie_stanga", false);
          setValue("cavitate_hipertrofie_dreapta", false);
          setValue("cavitate_hipertrofie_bilaterala", false);
        },
      });
    }
    // PIPA — Program Individualizat de Pregătire și Antrenament
    if (watchedValues.program_individualizat) {
      list.push({
        id: "program_individualizat",
        text: "Se recomandă program individualizat de pregătire și antrenament",
        clear: () => setValue("program_individualizat", false),
      });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedValues]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      if (data.completat && !data.data_completare) {
        data.data_completare = new Date().toISOString();
      }
      const updated = await patientApi.updateAtelier4(patientId, data);
      toast.success("Atelier 4 salvat cu succes!");
      setOpen(false);
      if (onUpdate) onUpdate(updated);
    } catch (error) {
      console.error("Failed to update atelier 4:", error);
      toast.error("Eroare la salvarea atelierului");
    } finally {
      setIsLoading(false);
    }
  };

  const isCompleted = atelierData?.completat;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant={isCompleted ? "default" : "outline"}
          className="gap-2 flex-1"
          data-testid="atelier-4-btn"
        >
          {isCompleted ? <Check className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
          Atelier 4
          {isCompleted && (
            <Badge variant="secondary" className="ml-1 text-xs">Completat</Badge>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            <Stethoscope className="w-5 h-5 text-primary" />
            Atelier 4 - Examen clinic pe sisteme
          </DialogTitle>
          {atelierData?.data_completare && (
            <p className="text-sm text-muted-foreground">
              Completat la: {formatDate(atelierData.data_completare)}
            </p>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
          {/* Asimptomatic — flag general afișat sus (dacă e bifat, apare în Diagnostic) */}
          <div className="flex items-center gap-2 p-3 rounded-lg border border-sky-200 bg-sky-50/40">
            <Checkbox
              id="asimptomatic"
              checked={watchedValues.asimptomatic}
              onCheckedChange={(c) => setValue("asimptomatic", !!c)}
              data-testid="atelier-4-asimptomatic"
            />
            <Label htmlFor="asimptomatic" className="text-sm font-medium cursor-pointer">
              Asimptomatic
            </Label>
            <span className="text-xs text-slate-500 ml-2">
              (dacă e bifat, apare &bdquo;Asimptomatic&rdquo; în &bdquo;Diagnostic&rdquo;)
            </span>
          </div>

          {/* Tegumente și mucoase */}
          {renderSistem(SISTEME[0], (
            <div className="flex items-center gap-2 p-2 rounded border bg-white">
              <Checkbox
                id="teg_dermatologie"
                checked={watchedValues.teg_dermatologie}
                onCheckedChange={(c) => setValue("teg_dermatologie", c)}
                data-testid="atelier-4-teg-derm"
              />
              <Label htmlFor="teg_dermatologie" className="text-sm cursor-pointer">
                Se recomandă control și tratament de specialitate serviciu Dermatologie
              </Label>
            </div>
          ))}

          {/* Țesut celular subcutanat - auto din IMC */}
          <Card className={
            watchedValues.tesut_status === "obezitate" ? "border-red-200" :
            watchedValues.tesut_status === "supraponderal" ? "border-orange-200" :
            watchedValues.tesut_status === "subponderal" ? "border-amber-200" :
            "border-emerald-200"
          }>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                {watchedValues.tesut_status === "normoponderal" ? (
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                <CardTitle className="text-base">Țesut celular subcutanat</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {watchedValues.tesut_descriere ? (
                <p className="text-sm font-medium">{watchedValues.tesut_descriere}</p>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  Completează IMC în Atelier 1 pentru evaluare automată.
                </p>
              )}
              {imc > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  IMC: {imc.toFixed(2)}{age !== null && ` · Vârstă: ${age} ani`}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Sistem ganglionar - fără recomandări */}
          {renderSistem(SISTEME[1], null)}

          {/* Aparat locomotor */}
          {renderSistem(SISTEME[2], (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <CheckOpt
                id="loc_repaus"
                label="Se recomandă repaus segmentar"
                checked={watchedValues.loc_repaus}
                onChange={(c) => setValue("loc_repaus", c)}
                testId="atelier-4-loc-repaus"
              >
                <div className="flex items-center gap-2 ml-6">
                  <Input
                    type="number"
                    placeholder="nr."
                    className="w-20"
                    {...register("loc_repaus_sapt")}
                  />
                  <span className="text-xs">săptămâni</span>
                </div>
              </CheckOpt>
              <CheckOpt
                id="loc_investigatii"
                label="Se recomandă investigații imagistice suplimentare"
                checked={watchedValues.loc_investigatii}
                onChange={(c) => setValue("loc_investigatii", c)}
                testId="atelier-4-loc-invest"
              >
                <Input
                  placeholder="Detalii investigații..."
                  className="ml-6 w-full"
                  {...register("loc_investigatii_text")}
                />
              </CheckOpt>
              <CheckOpt
                id="loc_reevaluare"
                label="Se recomandă reevaluare în"
                checked={watchedValues.loc_reevaluare}
                onChange={(c) => setValue("loc_reevaluare", c)}
                testId="atelier-4-loc-reeval"
              >
                <div className="flex items-center gap-2 ml-6">
                  <Input
                    type="number"
                    placeholder="nr."
                    className="w-20"
                    {...register("loc_reevaluare_sapt")}
                  />
                  <span className="text-xs">săptămâni</span>
                </div>
              </CheckOpt>
              <CheckOpt
                id="loc_ortopedie_ped"
                label="Se recomandă consult și tratament de specialitate serviciu Ortopedie pediatrică"
                checked={watchedValues.loc_ortopedie_ped}
                onChange={(c) => setValue("loc_ortopedie_ped", c)}
              />
              <CheckOpt
                id="loc_ortopedie"
                label="Se recomandă consult și tratament de specialitate serviciu Ortopedie"
                checked={watchedValues.loc_ortopedie}
                onChange={(c) => setValue("loc_ortopedie", c)}
              />
            </div>
          ))}

          {/* Aparat respirator */}
          {renderSistem(SISTEME[3], (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <CheckOpt
                id="resp_pediatrie"
                label="Se recomandă consult și tratament de specialitate serviciu Pediatrie"
                checked={watchedValues.resp_pediatrie}
                onChange={(c) => setValue("resp_pediatrie", c)}
              />
              <CheckOpt
                id="resp_rx"
                label="Se recomandă RX cord-pulmon"
                checked={watchedValues.resp_rx}
                onChange={(c) => setValue("resp_rx", c)}
              />
              <CheckOpt
                id="resp_probe"
                label="Se recomandă probe ventilatorii"
                checked={watchedValues.resp_probe}
                onChange={(c) => setValue("resp_probe", c)}
              />
              <CheckOpt
                id="resp_repaus"
                label="Se recomandă repaus, izolare și tratament la domiciliu"
                checked={watchedValues.resp_repaus}
                onChange={(c) => setValue("resp_repaus", c)}
              >
                <div className="flex items-center gap-2 ml-6">
                  <span className="text-xs">pe o perioadă de</span>
                  <Input
                    type="number"
                    placeholder="nr."
                    className="w-20"
                    {...register("resp_repaus_zile")}
                  />
                  <span className="text-xs">zile</span>
                </div>
              </CheckOpt>
            </div>
          ))}

          {/* Aparat cardiovascular */}
          {renderSistem(SISTEME[4], (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <CheckOpt
                id="cv_cardiologie_ped"
                label="Se recomandă consult de specialitate serviciu Cardiologie pediatrică"
                checked={watchedValues.cv_cardiologie_ped}
                onChange={(c) => setValue("cv_cardiologie_ped", c)}
              />
              <CheckOpt
                id="cv_cardiologie"
                label="Se recomandă consult de specialitate Cardiologie"
                checked={watchedValues.cv_cardiologie}
                onChange={(c) => setValue("cv_cardiologie", c)}
              />
              <CheckOpt
                id="cv_ecocardiografie"
                label="Se recomandă Ecocardiografie (Ecodoppler)"
                checked={watchedValues.cv_ecocardiografie}
                onChange={(c) => setValue("cv_ecocardiografie", c)}
              />
              <CheckOpt
                id="cv_holter_ekg"
                label="Se recomandă Holter EKG (24 de ore)"
                checked={watchedValues.cv_holter_ekg}
                onChange={(c) => setValue("cv_holter_ekg", c)}
              />
              <CheckOpt
                id="cv_holter_ta"
                label="Se recomandă Holter TA (24 de ore)"
                checked={watchedValues.cv_holter_ta}
                onChange={(c) => setValue("cv_holter_ta", c)}
              />
              <CheckOpt
                id="cv_in_observatie"
                label="În observație (suflu sistolic) — reevaluare semestrială"
                checked={watchedValues.cv_in_observatie}
                onChange={(c) => setValue("cv_in_observatie", c)}
              />
            </div>
          ))}

          {/* Aparat digestiv */}
          {renderSistem(SISTEME[5], (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <CheckOpt
                id="dig_ecografie"
                label="Se recomandă Ecografie abdominală"
                checked={watchedValues.dig_ecografie}
                onChange={(c) => setValue("dig_ecografie", c)}
              />
              <CheckOpt
                id="dig_gastro"
                label="Se recomandă control de specialitate Gastroenterologie"
                checked={watchedValues.dig_gastro}
                onChange={(c) => setValue("dig_gastro", c)}
              />
            </div>
          ))}

          {/* Aparat urogenital */}
          {renderSistem(SISTEME[6], (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <CheckOpt
                id="uro_urologie"
                label="Se recomandă control de specialitate serviciu Urologie"
                checked={watchedValues.uro_urologie}
                onChange={(c) => setValue("uro_urologie", c)}
              />
              <CheckOpt
                id="uro_nefrologie"
                label="Se recomandă control de specialitate serviciu Nefrologie"
                checked={watchedValues.uro_nefrologie}
                onChange={(c) => setValue("uro_nefrologie", c)}
              />
            </div>
          ))}

          {/* Sistem endocrin */}
          {renderSistem(SISTEME[7], (
            <div className="flex items-center gap-2 p-2 rounded border bg-white">
              <Checkbox
                id="endo_endocrinologie"
                checked={watchedValues.endo_endocrinologie}
                onCheckedChange={(c) => setValue("endo_endocrinologie", c)}
              />
              <Label htmlFor="endo_endocrinologie" className="text-sm cursor-pointer">
                Control de specialitate Endocrinologie
              </Label>
            </div>
          ))}

          {/* ---- ACUITATE VIZUALĂ ---- */}
          {(() => {
            const isModif = watchedValues.acuitate_vizuala_status === "Modificat";
            return (
              <Card className={isModif ? "border-red-200" : "border-emerald-200"} data-testid="atelier-4-acuitate-vizuala">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      {isModif ? <XCircle className="w-5 h-5 text-red-500" /> : <CheckCircle className="w-5 h-5 text-emerald-500" />}
                      <CardTitle className="text-base">Acuitate vizuală</CardTitle>
                    </div>
                    <div className="flex gap-1">
                      <Button type="button" size="sm"
                        variant={!isModif ? "default" : "outline"}
                        onClick={() => setValue("acuitate_vizuala_status", "Normal")}
                        data-testid="atelier-4-acuitate-normal">
                        Normal
                      </Button>
                      <Button type="button" size="sm"
                        variant={isModif ? "destructive" : "outline"}
                        onClick={() => setValue("acuitate_vizuala_status", "Modificat")}
                        data-testid="atelier-4-acuitate-modif">
                        Modifică
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                {isModif && (
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-1 gap-2">
                      <label className="flex items-center gap-2 p-2 rounded border bg-white cursor-pointer">
                        <input type="radio" name="acuitate_optie" checked={watchedValues.acuitate_vizuala_optie === "corectie_existenta"}
                          onChange={() => setValue("acuitate_vizuala_optie", "corectie_existenta")}
                          data-testid="acuitate-corectie-existenta" />
                        <span className="text-sm">Corecție optică existentă</span>
                      </label>
                      {watchedValues.acuitate_vizuala_optie === "corectie_existenta" && (
                        <div className="grid grid-cols-2 gap-2 pl-6">
                          <div>
                            <Label htmlFor="ac_od" className="text-xs">OD</Label>
                            <Input id="ac_od" placeholder="ex: -1.25 sf" {...register("acuitate_vizuala_od")} data-testid="acuitate-od" />
                          </div>
                          <div>
                            <Label htmlFor="ac_os" className="text-xs">OS</Label>
                            <Input id="ac_os" placeholder="ex: -1.00 sf" {...register("acuitate_vizuala_os")} data-testid="acuitate-os" />
                          </div>
                        </div>
                      )}
                      <label className="flex items-center gap-2 p-2 rounded border bg-white cursor-pointer">
                        <input type="radio" name="acuitate_optie" checked={watchedValues.acuitate_vizuala_optie === "necesita_corectie"}
                          onChange={() => setValue("acuitate_vizuala_optie", "necesita_corectie")}
                          data-testid="acuitate-necesita-corectie" />
                        <span className="text-sm">Necesită corecție optică</span>
                      </label>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })()}

          {/* ---- VEDERE CROMATICĂ (Normal / Discromat) ---- */}
          {(() => {
            const isDisc = watchedValues.vedere_cromatica_status === "Discromat";
            return (
              <Card className={isDisc ? "border-amber-300" : "border-emerald-200"} data-testid="atelier-4-vedere-cromatica">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      {isDisc ? <AlertCircle className="w-5 h-5 text-amber-600" /> : <CheckCircle className="w-5 h-5 text-emerald-500" />}
                      <CardTitle className="text-base">Vedere cromatică</CardTitle>
                    </div>
                    <div className="flex gap-1">
                      <Button type="button" size="sm"
                        variant={!isDisc ? "default" : "outline"}
                        onClick={() => setValue("vedere_cromatica_status", "Normal")}
                        data-testid="atelier-4-cromatica-normal">Normal</Button>
                      <Button type="button" size="sm"
                        variant={isDisc ? "default" : "outline"}
                        className={isDisc ? "bg-amber-600 hover:bg-amber-700" : ""}
                        onClick={() => setValue("vedere_cromatica_status", "Discromat")}
                        data-testid="atelier-4-cromatica-discromat">Discromat</Button>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            );
          })()}

          {/* ---- SISTEM NERVOS ---- */}
          {(() => {
            const isModif = watchedValues.sistem_nervos_status === "Modificat";
            return (
              <Card className={isModif ? "border-red-200" : "border-emerald-200"} data-testid="atelier-4-sistem-nervos">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      {isModif ? <XCircle className="w-5 h-5 text-red-500" /> : <CheckCircle className="w-5 h-5 text-emerald-500" />}
                      <CardTitle className="text-base">Sistem nervos</CardTitle>
                    </div>
                    <div className="flex gap-1">
                      <Button type="button" size="sm"
                        variant={!isModif ? "default" : "outline"}
                        onClick={() => setValue("sistem_nervos_status", "Normal")}
                        data-testid="atelier-4-sn-normal">Normal</Button>
                      <Button type="button" size="sm"
                        variant={isModif ? "destructive" : "outline"}
                        onClick={() => setValue("sistem_nervos_status", "Modificat")}
                        data-testid="atelier-4-sn-modif">Modifică</Button>
                    </div>
                  </div>
                </CardHeader>
                {!isModif ? (
                  <CardContent className="pt-0">
                    <p className="text-sm text-slate-700 italic">
                      Orientat temporo-spațial conștient, cooperant, ROT prezente egale și simetrice bilateral, sensibilitate păstrată fără tulburări de vorbire.
                    </p>
                  </CardContent>
                ) : (
                  <CardContent className="space-y-2">
                    <label className="flex items-start gap-2 p-2 rounded border bg-white cursor-pointer">
                      <Checkbox
                        checked={watchedValues.sistem_nervos_afectiuni_preexistente}
                        onCheckedChange={(c) => setValue("sistem_nervos_afectiuni_preexistente", c)}
                        data-testid="sn-preexistente"
                        className="mt-0.5"
                      />
                      <span className="text-sm">Afecțiuni preexistente ale SNC sau SNP ce sunt în observație și/sau tratament</span>
                    </label>
                    <label className="flex items-start gap-2 p-2 rounded border bg-white cursor-pointer">
                      <Checkbox
                        checked={watchedValues.sistem_nervos_debut_recent}
                        onCheckedChange={(c) => setValue("sistem_nervos_debut_recent", c)}
                        data-testid="sn-debut-recent"
                        className="mt-0.5"
                      />
                      <span className="text-sm">Afecțiuni ale SNC sau SNP cu debut recent</span>
                    </label>
                  </CardContent>
                )}
              </Card>
            );
          })()}

          {/* ---- PROBA ROMBERG (Pozitiv / Negativ) ---- */}
          {(() => {
            const isPoz = watchedValues.proba_romberg_status === "Pozitiv";
            return (
              <Card className={isPoz ? "border-red-200" : "border-emerald-200"} data-testid="atelier-4-romberg">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      {isPoz ? <XCircle className="w-5 h-5 text-red-500" /> : <CheckCircle className="w-5 h-5 text-emerald-500" />}
                      <CardTitle className="text-base">Proba Romberg</CardTitle>
                    </div>
                    <div className="flex gap-1">
                      <Button type="button" size="sm"
                        variant={!isPoz ? "default" : "outline"}
                        onClick={() => setValue("proba_romberg_status", "Negativ")}
                        data-testid="atelier-4-romberg-negativ">Negativ</Button>
                      <Button type="button" size="sm"
                        variant={isPoz ? "destructive" : "outline"}
                        onClick={() => setValue("proba_romberg_status", "Pozitiv")}
                        data-testid="atelier-4-romberg-pozitiv">Pozitiv</Button>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            );
          })()}

          {/* ---- EXAMEN CAVITATE ORALĂ ---- */}
          {(() => {
            const isModif = watchedValues.cavitate_orala_status === "Modificat";
            return (
              <Card className={isModif ? "border-red-200" : "border-emerald-200"} data-testid="atelier-4-cavitate-orala">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      {isModif ? <XCircle className="w-5 h-5 text-red-500" /> : <CheckCircle className="w-5 h-5 text-emerald-500" />}
                      <CardTitle className="text-base">Examenul cavității orale</CardTitle>
                    </div>
                    <div className="flex gap-1">
                      <Button type="button" size="sm"
                        variant={!isModif ? "default" : "outline"}
                        onClick={() => setValue("cavitate_orala_status", "Normal")}
                        data-testid="atelier-4-cavitate-normal">Normal</Button>
                      <Button type="button" size="sm"
                        variant={isModif ? "destructive" : "outline"}
                        onClick={() => setValue("cavitate_orala_status", "Modificat")}
                        data-testid="atelier-4-cavitate-modif">Modifică</Button>
                    </div>
                  </div>
                </CardHeader>
                {isModif && (
                  <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                    {[
                      ["cavitate_carii_superficiale", "Carii superficiale"],
                      ["cavitate_carii_profunde", "Carii profunde"],
                      ["cavitate_aparat_ortodontic", "Aparat ortodontic"],
                      ["cavitate_eritem_faringe", "Eritem faringe posterior"],
                      ["cavitate_hipertrofie_stanga", "Hipertrofie amigdaliană stângă"],
                      ["cavitate_hipertrofie_dreapta", "Hipertrofie amigdaliană dreaptă"],
                      ["cavitate_hipertrofie_bilaterala", "Hipertrofie amigdaliană bilaterală"],
                    ].map(([field, label]) => (
                      <label key={field} className="flex items-start gap-2 p-2 rounded border bg-white cursor-pointer">
                        <Checkbox
                          checked={watchedValues[field]}
                          onCheckedChange={(c) => setValue(field, c)}
                          className="mt-0.5"
                          data-testid={field}
                        />
                        <span className="text-sm">{label}</span>
                      </label>
                    ))}
                  </CardContent>
                )}
              </Card>
            );
          })()}

          <Separator />

          {/* PIPA — Program Individualizat de Pregătire și Antrenament */}
          <div className="flex items-center justify-between gap-3 p-3 rounded-md border bg-white" data-testid="atelier-4-pipa-container">
            <div className="min-w-0">
              <div className="text-sm font-medium">Program Individualizat de Pregătire și Antrenament (PIPA)</div>
              <div className="text-xs text-muted-foreground">Când e activ, adaugă recomandarea în Atelier 4, Referat medical și Concluzii aviz medico-sportiv.</div>
            </div>
            <Button
              type="button"
              onClick={() => setValue("program_individualizat", !watchedValues.program_individualizat)}
              variant={watchedValues.program_individualizat ? "default" : "outline"}
              className={
                watchedValues.program_individualizat
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white gap-2 shrink-0"
                  : "bg-white gap-2 shrink-0"
              }
              data-testid="atelier-4-pipa-btn"
            >
              {watchedValues.program_individualizat ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {watchedValues.program_individualizat ? "PIPA activ" : "Adaugă PIPA"}
            </Button>
          </div>

          <Separator />

          {/* Recomandări */}
          {recomandari.length > 0 && (
            <Card className="border-primary/30 bg-primary/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-primary" />
                  Recomandări ({recomandari.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2" data-testid="atelier-4-recomandari">
                  {recomandari.map((r) => (
                    <li
                      key={r.id}
                      className="flex items-start justify-between gap-3 p-3 rounded bg-white border"
                    >
                      <span className="text-sm flex-1">{r.text}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={r.clear}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 gap-1 h-7"
                        data-testid={`atelier-4-sterge-${r.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                        Șterge recomandare
                      </Button>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Sănătos clinic — dacă e bifat, Diagnosticul din Concluzii = „Sănătos clinic" */}
          <div className="flex items-center gap-2 p-3 rounded-lg border border-emerald-200 bg-emerald-50/40">
            <Checkbox
              id="normal_clinic"
              checked={watchedValues.normal_clinic}
              onCheckedChange={(c) => setValue("normal_clinic", !!c)}
              data-testid="atelier-4-normal-clinic"
            />
            <Label htmlFor="normal_clinic" className="text-sm font-medium cursor-pointer">
              Normal clinic
            </Label>
            <span className="text-xs text-slate-500 ml-2">
              (dacă e bifat, în &bdquo;Diagnostic&rdquo; se va scrie &bdquo;Sănătos clinic&rdquo;)
            </span>
          </div>

          {/* Observații */}
          <div className="space-y-2">
            <Label htmlFor="observatii-4">Observații Suplimentare</Label>
            <Textarea
              id="observatii-4"
              data-testid="atelier-4-observatii"
              rows={3}
              placeholder="Adăugați observații suplimentare..."
              {...register("observatii")}
            />
          </div>

          {/* Recap: recomandări/observații din celelalte ateliere */}
          {(() => {
            const otherItems = buildOtherAteliereItems(patientData);
            if (otherItems.length === 0) return null;
            const apiMap = { 1: patientApi.updateAtelier1, 2: patientApi.updateAtelier2, 3: patientApi.updateAtelier3, 5: patientApi.updateAtelier5 };
            const handleDelete = async (it) => {
              setDeletingId(it.id);
              try {
                const updated = await apiMap[it.atelier](patientId, it.patch);
                onUpdate?.(updated);
                toast.success("Recomandare ștearsă");
              } catch (e) {
                console.error("[Atelier4 recap] delete error", e);
                toast.error("Eroare la ștergere");
              } finally {
                setDeletingId(null);
              }
            };
            return (
              <div className="pt-4 border-t" data-testid="atelier-4-other-recs">
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-primary" />
                  Recomandări/observații din celelalte ateliere:
                </h4>
                <div className="space-y-1.5">
                  {otherItems.map((it) => (
                    <div
                      key={it.id}
                      className="flex items-start gap-2 p-2 rounded border border-slate-200 bg-slate-50"
                      data-testid={`other-rec-${it.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">{it.source}</div>
                        <div className="text-sm text-slate-800 whitespace-pre-wrap break-words">{it.text}</div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0 text-slate-500 hover:text-red-600 hover:bg-red-50"
                        disabled={deletingId === it.id}
                        onClick={() => handleDelete(it)}
                        title="Șterge recomandare"
                        data-testid={`other-rec-del-${it.id}`}
                      >
                        {deletingId === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-3 pt-4 border-t">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="completat-4"
                data-testid="atelier-4-completat"
                checked={watchedValues.completat}
                onCheckedChange={(checked) => setValue("completat", checked)}
              />
              <Label htmlFor="completat-4" className="text-sm font-medium cursor-pointer">
                Marchează ca și completat
              </Label>
            </div>
            <div className="flex-1" />
            <GenerateVizaButton patient={patientData} onGenerated={onUpdate} />
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Anulează
            </Button>
            <Button type="submit" disabled={isLoading} data-testid="atelier-4-save">
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvează
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
